import classNames from "classnames"
import React from "react"

import Badge from "@components/elements/V2/Badge"
import CheckIcon from "@components/elements/V2/Icons/CheckIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { FeatureCellValue } from "@constants/V2/planComparison"
import { getTextColorClass } from "@utils/V2/color"

type Props = {
  value: FeatureCellValue
  textColor: Color
  className?: string
  // Rate cells only: collapse the per-cell note on mobile so the row can render
  // a single note line spanning all three plan columns instead (see index.tsx).
  hideNoteOnMobile?: boolean
}

const StringCellValue = ({
  value,
  textColor,
}: {
  value: string
  textColor: Color
}) => {
  const mobileValue = value.replace("/month", " pm")

  if (mobileValue === value) {
    return (
      <Typography
        font="grotesk"
        weight="book"
        size="body-md"
        color={textColor}
        text={value}
        className="text-center"
      />
    )
  }

  return (
    <>
      <Typography
        font="grotesk"
        weight="book"
        size="body-md"
        color={textColor}
        text={mobileValue}
        className="text-center sm:hidden"
      />
      <Typography
        font="grotesk"
        weight="book"
        size="body-md"
        color={textColor}
        text={value}
        className="hidden text-center sm:block"
      />
    </>
  )
}

const RateCellValue = ({
  rate,
  note,
  textColor,
  hideNoteOnMobile,
}: {
  rate: string
  note?: string
  textColor: Color
  hideNoteOnMobile?: boolean
}) => (
  <div className="flex flex-col items-center justify-center gap-4">
    <Typography
      font="grotesk"
      weight="book"
      size="body-md"
      color={textColor}
      text={rate}
      className="text-center"
    />
    {note && (
      <Typography
        font="grotesk"
        weight="book"
        size="subscript-sm"
        color={Color.Charcoal}
        text={note}
        className={classNames(
          "text-center opacity-80",
          hideNoteOnMobile && "hidden sm:block"
        )}
      />
    )}
  </div>
)

const Cell = ({ value, textColor, className, hideNoteOnMobile }: Props) => (
  <div className={classNames("flex items-center justify-center", className)}>
    {typeof value === "boolean" ? (
      value ? (
        <CheckIcon
          className={classNames("h-24 w-24", getTextColorClass(Color.Blue))}
        />
      ) : (
        <Typography
          font="grotesk"
          weight="book"
          size="body-md"
          color={Color.Charcoal}
          text="-"
          className="opacity-80"
        />
      )
    ) : typeof value === "string" ? (
      <StringCellValue value={value} textColor={textColor} />
    ) : "rate" in value ? (
      <RateCellValue
        rate={value.rate}
        note={value.note}
        textColor={textColor}
        hideNoteOnMobile={hideNoteOnMobile}
      />
    ) : (
      <Badge
        text={value.badge}
        style="solid"
        textColor={Color.White}
        accentColor={Color.Charcoal}
        uppercaseText={false}
        isRounded
        size="subscript-sm"
      />
    )}
  </div>
)

export default Cell
